import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditFormField } from '../../../EditFormField';
import type { ContactSocialMediaData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPSERT_CONTACT_SOCIAL_MEDIA = gql`
  mutation UpsertContactSocialMedia(
    $contactId: ID!
    $values: ContactSocialMediaInput!
  ) {
    upsertContactSocialMedia(contactId: $contactId, values: $values) {
      ok
      created
      contactSocialMedia {
        id
        linkedin
        linkedinUrl
        onLinkedin
        twitter
        facebookUrl
        onFacebook
      }
    }
  }
`;

const SOCIAL_MEDIA_FIELDS: Record<
  string,
  { apiField: string; isBoolean?: boolean }
> = {
  facebookUrl: { apiField: 'facebookUrl' },
  linkedin: { apiField: 'linkedin' },
  linkedinUrl: { apiField: 'linkedinUrl' },
  onFacebook: { apiField: 'onFacebook', isBoolean: true },
  onLinkedin: { apiField: 'onLinkedin', isBoolean: true },
  twitter: { apiField: 'twitter' }
};

const urlSchema = z.string().refine(
  val => {
    if (val === '') return true;
    return z.url().safeParse(val).success;
  },
  {
    message: 'Please enter a valid URL (e.g., https://example.com)'
  }
);

const socialMediaSchema = z.object({
  facebookUrl: urlSchema,
  linkedin: z.string(),
  linkedinUrl: urlSchema,
  twitter: z.string()
});

export interface EditContactSocialMediaDialogProps {
  contactId: string;
  initialValue: ContactSocialMediaData;
  isOpen: boolean;
  onChange: (data: ContactSocialMediaData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactSocialMediaData;
}

export function EditContactSocialMediaDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditContactSocialMediaDialogProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  const [upsertSocialMedia] = useMutation(UPSERT_CONTACT_SOCIAL_MEDIA, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const validateField = (field: string, fieldValue: string) => {
    // Only validate URL fields
    if (field === 'facebookUrl' || field === 'linkedinUrl') {
      const result = urlSchema.safeParse(fieldValue);
      if (!result.success && result.error.issues.length > 0) {
        setErrors(prev => ({
          ...prev,
          [field]: result.error.issues[0]?.message ?? 'Invalid URL'
        }));
      } else {
        setErrors(prev => {
          const newErrors = { ...prev };
          // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const updateField = <K extends keyof ContactSocialMediaData>(
    field: K,
    fieldValue: ContactSocialMediaData[K]
  ) => {
    const stringValue = fieldValue as string;
    validateField(field as string, stringValue);
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    // Validate all URL fields first
    const result = socialMediaSchema.safeParse({
      facebookUrl: value.facebookUrl,
      linkedin: value.linkedin,
      linkedinUrl: value.linkedinUrl,
      twitter: value.twitter
    });

    if (!result.success) {
      const newErrors: Record<string, string> = {};
      result.error.issues.forEach(issue => {
        const path = issue.path[0];
        if (typeof path === 'string') {
          newErrors[path] = issue.message;
        }
      });
      setErrors(newErrors);
      return;
    }

    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await upsertSocialMedia({
        variables: { contactId, values: changes }
      });

      const data = response.data as {
        upsertContactSocialMedia?: { ok?: boolean };
      };

      if (data.upsertContactSocialMedia?.ok === false) {
        toast.error('Failed to save social media');
      } else {
        setErrors({});
        toast.success('Social media saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving social media:', error);
      toast.error('Failed to save social media');
    } finally {
      setIsSaving(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setErrors({});
    }
    onOpenChange(open);
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={handleOpenChange}
      onSave={handleSave}
      title="Social Media"
    >
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <EditFormField
          error={errors.facebookUrl}
          label="Facebook Url"
          onChange={v => {
            updateField('facebookUrl', v);
          }}
          placeholder="Enter Facebook URL"
          type="url"
          value={value.facebookUrl}
        />
        <EditCheckboxField
          checked={value.onFacebook}
          label="On Facebook?"
          onCheckedChange={v => {
            updateField('onFacebook', v);
          }}
        />
        <EditFormField
          label="LinkedIn"
          onChange={v => {
            updateField('linkedin', v);
          }}
          placeholder="Enter LinkedIn username or URL"
          value={value.linkedin}
        />
        <EditFormField
          error={errors.linkedinUrl}
          label="LinkedIn Url"
          onChange={v => {
            updateField('linkedinUrl', v);
          }}
          placeholder="Enter LinkedIn URL"
          type="url"
          value={value.linkedinUrl}
        />
        <EditCheckboxField
          checked={value.onLinkedin}
          label="On LinkedIn?"
          onCheckedChange={v => {
            updateField('onLinkedin', v);
          }}
        />
        <EditFormField
          label="Twitter"
          onChange={v => {
            updateField('twitter', v);
          }}
          placeholder="Enter Twitter username or URL"
          value={value.twitter}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactSocialMediaData,
  initial: ContactSocialMediaData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(SOCIAL_MEDIA_FIELDS)) {
    const currentVal = current[uiField as keyof ContactSocialMediaData];
    const initialVal = initial[uiField as keyof ContactSocialMediaData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else {
        const strVal = currentVal as string;
        changes[config.apiField] = strVal === '' ? null : strVal;
      }
    }
  }

  return changes;
}
